import React from 'react';
import { Formation, Competence } from '../../../../../../shared/types/config';

interface FormationModalProps {
  editingFormation: Formation | null;
  formationForm: Omit<Formation, 'id' | 'competence_nom'>;
  setFormationForm: (form: Omit<Formation, 'id' | 'competence_nom'>) => void;
  competences: Competence[];
  onClose: () => void;
  onSave: () => void;
}

const inputCls = 'w-full rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#3d4fdb]/60';
const labelCls = 'block text-xs font-medium text-slate-400 mb-1.5';

const FormationModal: React.FC<FormationModalProps> = ({
  editingFormation,
  formationForm,
  setFormationForm,
  competences,
  onClose,
  onSave,
}) => {
  const set = (field: keyof typeof formationForm, value: unknown) =>
    setFormationForm({ ...formationForm, [field]: value });

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="rounded-xl border border-[#243052]/90 bg-[#0a1128] shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1e2d4d]">
          <h2 className="text-base font-bold text-white">
            {editingFormation ? 'Modifier la formation' : 'Nouvelle formation'}
          </h2>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white hover:bg-[#1e2d4d] rounded-lg transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className={labelCls}>Nom *</label>
            <input type="text" value={formationForm.nom} onChange={(e) => set('nom', e.target.value)} placeholder="Ex : CA INC 1" className={inputCls} />
          </div>

          <div>
            <label className={labelCls}>Description</label>
            <textarea
              value={formationForm.description || ''}
              onChange={(e) => set('description', e.target.value)}
              rows={2}
              className={`${inputCls} resize-none`}
            />
          </div>

          <div>
            <label className={labelCls}>Compétence obtenue</label>
            <select
              value={formationForm.config_competence_id ?? ''}
              onChange={(e) => set('config_competence_id', e.target.value ? Number(e.target.value) : null)}
              className={inputCls}
            >
              <option value="">— Aucune compétence liée —</option>
              {competences.map((c) => (
                <option key={c.id} value={c.id}>{c.nom}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Durée (heures)</label>
              <input type="number" min={1} value={formationForm.duree_heures} onChange={(e) => set('duree_heures', Number(e.target.value) || 8)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Prix (€)</label>
              <input type="number" min={0} value={formationForm.prix} onChange={(e) => set('prix', Number(e.target.value) || 0)} className={inputCls} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Ordre</label>
              <input type="number" min={0} value={formationForm.ordre} onChange={(e) => set('ordre', Number(e.target.value) || 0)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Niveau min. requis</label>
              <input type="number" min={1} value={formationForm.niveau_personnel_requis} onChange={(e) => set('niveau_personnel_requis', Number(e.target.value) || 1)} className={inputCls} />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="formation-actif"
              checked={formationForm.actif}
              onChange={(e) => set('actif', e.target.checked)}
              className="w-4 h-4 rounded border-[#2a3a62] bg-[#0a132b]"
            />
            <label htmlFor="formation-actif" className="text-sm text-slate-300">Formation active</label>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#1e2d4d]">
          <button
            onClick={onClose}
            className="rounded-lg border border-white/15 bg-black/30 px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-black/45 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onSave}
            className="rounded-lg border border-[#3d4fdb]/40 bg-[#3d4fdb]/20 px-4 py-2 text-sm font-semibold text-[#8ea0f5] hover:bg-[#3d4fdb]/30 transition-colors"
          >
            {editingFormation ? 'Modifier' : 'Créer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormationModal;
